import type { TimelineHighlight } from '@lib/api-types/views';
import { useEffect, useMemo, useRef, useState } from 'react';

import { buildStopAxis, stepStop } from '../timeline-stops.ts';

// (The old TICK_HOVER_PX proximity gate is gone: every point on the track now
// resolves to a stop, so hover previews the nearest one rather than only
// lighting up within a few pixels of a hairline. See `nearestStop`.)

interface Props {
  /** One entry per snapshot date — the lightweight timeline axis. `highlight`
   *  is what makes an entry an ERA, and therefore a navigable stop; `date`
   *  places that stop on the track. Payloads load lazily elsewhere. */
  snapshots: ReadonlyArray<{ date: string; highlight?: TimelineHighlight | null }>;
  /** Currently selected snapshot index — a raw position in `snapshots`, not a
   *  position in the stop list. */
  index: number;
  /** Fires as the user clicks / drags the track, with a raw `snapshots` index
   *  (always one of the stops). */
  onIndexChange: (next: number) => void;
  /** Mon-DD-YYYY formatter (shared with the page so labels match). */
  formatDate: (date: string | null | undefined) => string;
}

function PlayIcon() {
  return (
    <svg width='14' height='14' viewBox='0 0 14 14' aria-hidden='true'>
      <path d='M4 2.5v9l8-4.5-8-4.5z' fill='currentColor' />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg width='14' height='14' viewBox='0 0 14 14' aria-hidden='true'>
      <rect x='3.5' y='2.5' width='2.5' height='9' rx='0.5' fill='currentColor' />
      <rect x='8' y='2.5' width='2.5' height='9' rx='0.5' fill='currentColor' />
    </svg>
  );
}

/**
 * Bottom timeline scrubber for the history page. A track fixed over the
 * platform map, navigating the ERA SNAPSHOTS — the dates carrying a narrative
 * highlight — plus the newest snapshot, so "now" stays reachable.
 *
 *  - default: a blue cursor line sits on the selected stop (the page defaults
 *    it to the latest).
 *  - hover → the stop you would land on darkens and surfaces its date.
 *  - click / drag → snaps to that stop; the cursor lands exactly on its tick.
 *  - play → tours the stops in order, 2.5s each; the button toggles to pause
 *    and any interaction (or reaching the last stop) stops it there.
 *
 * ONE POSITION FUNCTION, AND THAT IS THE WHOLE FIX. This track used to draw
 * two coordinate systems at once: plain snapshot ticks and the cursor were
 * spaced EVENLY by array index (`i / max`), while a highlight's stroke was
 * placed at its TRUE event date (`highlight.at`). The two disagreed by however
 * far an era's headline moment sat from the snapshot that owned it — up to a
 * whole tick's cadence, which on a long-history org is a year. Everything the
 * user reported follows from that single split: clicking a stroke selected its
 * owning snapshot and the cursor then materialised somewhere else entirely
 * (the "jumpiness"), a click a few pixels off a stroke fell through to
 * `Math.round(f * max)` and landed on an unrelated index ("ticks don't
 * navigate to the right date"), and the date on screen belonged to the owning
 * snapshot while the stroke under it belonged to the event ("the date doesn't
 * match the scrubber").
 *
 * So position is now derived ONCE, by {@link posFrac}, from the date of the
 * snapshot a stop actually navigates to — ticks, cursor and tooltip all read
 * it. Hover, click and the rendered matrix cannot disagree, because there is
 * no longer a second coordinate system for them to disagree in.
 *
 * The regular (non-era) intervals are no longer drawn or reachable. They were
 * never separately meaningful — the composer emits them on a fixed cadence —
 * and stepping onto one showed a matrix with no narrative to explain it.
 */
export function TimelineScrubber({
  snapshots,
  index,
  onIndexChange,
  formatDate,
}: Props) {
  const lastIndex = Math.max(0, snapshots.length - 1);
  const safeIndex = Math.min(Math.max(0, index), lastIndex);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [dragging, setDragging] = useState(false);
  // Snapshot index of the stop the cursor would land on while hovering (not
  // dragging) — drives the tick highlight + the date tooltip. One field, not
  // the old tick/highlight pair: there is only one kind of stop now.
  const [hoverStop, setHoverStop] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);

  // The navigable stops + the single position function, both from
  // `timeline-stops.ts` where they're unit-tested. `stops` is the era
  // snapshots plus the newest; `posFrac` places any snapshot index on the
  // track by ITS OWN date. See that module for why each is shaped this way.
  const { stops, posFrac, nearestStop: nearestStopAt, firstDate, lastDate } = useMemo(
    () => buildStopAxis(snapshots),
    [snapshots],
  );

  // Autoplay: hop to the next stop every 2.5s (long enough to read its
  // narrative), then stop on the last one. The effect re-runs each time `index`
  // changes (the prior hop's advance), scheduling the next hop.
  useEffect(() => {
    if (!playing) return;
    const next = stops.find((i) => i > safeIndex);
    if (next === undefined) {
      setPlaying(false);
      return;
    }
    const t = setTimeout(() => onIndexChange(next), 2500);
    return () => clearTimeout(t);
  }, [playing, safeIndex, stops, onIndexChange]);

  function handlePlayPause() {
    if (playing) {
      setPlaying(false);
      return;
    }
    const first = stops[0];
    if (first === undefined) return; // nothing to tour
    onIndexChange(first); // start the tour at the earliest stop
    setPlaying(true);
  }

  /** The stop nearest the pointer, in the track's own coordinates. */
  function nearestStop(clientX: number): number | null {
    const el = trackRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return nearestStopAt(clientX - rect.left, rect.width);
  }

  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (stops.length === 0) return;
    setPlaying(false); // grabbing the track stops autoplay
    setDragging(true);
    trackRef.current?.setPointerCapture(e.pointerId);
    const stop = nearestStop(e.clientX);
    if (stop != null) onIndexChange(stop);
  }

  function handlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const stop = nearestStop(e.clientX);
    if (dragging) {
      if (stop != null) onIndexChange(stop);
      return;
    }
    // Hover previews exactly what a click would select — same function, so the
    // two cannot drift apart.
    setHoverStop(stop);
  }

  function handlePointerUp(e: React.PointerEvent<HTMLDivElement>) {
    if (!dragging) return;
    setDragging(false);
    trackRef.current?.releasePointerCapture(e.pointerId);
  }

  function handlePointerLeave() {
    if (!dragging) setHoverStop(null);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    // Arrow keys step era-to-era, like every other interaction here.
    let dir: 'prev' | 'next' | 'first' | 'last' | null = null;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') dir = 'prev';
    else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') dir = 'next';
    else if (e.key === 'Home') dir = 'first';
    else if (e.key === 'End') dir = 'last';
    if (dir == null) return;
    e.preventDefault();
    const next = stepStop(stops, safeIndex, dir);
    if (next == null) return;
    setPlaying(false); // keyboard scrubbing stops autoplay
    onIndexChange(next);
  }

  // Tooltip target — just a date bubble, over the stop it refers to. While
  // dragging / auto-playing it tracks the live selection; otherwise the hovered
  // stop. Both the bubble's position and its date come from that stop's
  // snapshot, so what the tooltip says is what the click navigates to and what
  // the matrix then renders. The narrative (title + summary) lives in the
  // right-hand panel, not here.
  const tipStop = dragging || playing ? safeIndex : hoverStop;
  const tooltip: { frac: number; date: string | null | undefined } | null =
    tipStop != null ? { frac: posFrac(tipStop), date: snapshots[tipStop]?.date } : null;

  return (
    <div className='snap-scrubber'>
      <button
        type='button'
        className='snap-scrubber__play'
        aria-label={playing ? 'Pause timeline' : 'Play timeline'}
        onClick={handlePlayPause}
      >
        {playing ? <PauseIcon /> : <PlayIcon />}
      </button>
      <span className='snap-scrubber__date'>{formatDate(firstDate)}</span>
      <div className='snap-scrubber__track-wrap'>
        <div
          ref={trackRef}
          className='snap-scrubber__track'
          role='slider'
          tabIndex={0}
          aria-label='Era snapshot'
          // The slider's value space is the STOP LIST, not the raw axis — an
          // assistive reader stepping this control moves era-to-era, the same
          // positions the arrow keys and the ticks address.
          aria-valuemin={0}
          aria-valuemax={Math.max(0, stops.length - 1)}
          aria-valuenow={Math.max(0, stops.indexOf(safeIndex))}
          aria-valuetext={formatDate(snapshots[safeIndex]?.date)}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerLeave}
          onKeyDown={handleKeyDown}
        >
          {/* One tick per navigable stop, at its snapshot's true date. The
              regular (non-era) intervals are deliberately not drawn — see the
              component doc. */}
          {stops.map((s) => (
            <span
              key={s}
              className={`snap-scrubber__tick snap-scrubber__tick--highlight${
                !dragging && s === hoverStop ? ' is-hover' : ''
              }`}
              style={{ left: `${posFrac(s) * 100}%` }}
            />
          ))}
          <span
            className='snap-scrubber__cursor'
            style={{ left: `${posFrac(safeIndex) * 100}%` }}
          />
        </div>
        {tooltip != null ? (
          <div className='snap-scrubber__tooltip' style={{ left: `${tooltip.frac * 100}%` }}>
            {formatDate(tooltip.date)}
          </div>
        ) : null}
      </div>
      <span className='snap-scrubber__date'>{formatDate(lastDate)}</span>
    </div>
  );
}
